import React, { useMemo, useState } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { format, startOfDay, subDays, subMonths, subWeeks } from "date-fns"
import { Clock, Hash } from "lucide-react"

import type { DateRangeUnit } from "@/utils/date/dateRangeTypes"

export interface DynamicDateRange {
  type: "dynamic"
  value: number
  unit: DateRangeUnit
  displayLabel: string
  startDate: Date
  endDate: Date
}

interface DynamicDateRangeInputProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback when modal should close */
  onClose: () => void
  /** Callback when dynamic date range is selected */
  onDateRangeSelect: (dynamicRange: DynamicDateRange) => void
  /** Initial values */
  initialValue?: number
  initialUnit?: DateRangeUnit
}

const MAX_UNITS = 999

const TIME_UNITS = [
  { key: "days", label: "Days", plural: "days", singular: "day" },
  { key: "weeks", label: "Weeks", plural: "weeks", singular: "week" },
  { key: "months", label: "Months", plural: "months", singular: "month" },
] as const

export const DynamicDateRangeInput: React.FC<DynamicDateRangeInputProps> = ({
  isOpen,
  onClose,
  onDateRangeSelect,
  initialValue = 7,
  initialUnit = "days",
}) => {
  const [value, setValue] = useState<string>(initialValue.toString())
  const [unit, setUnit] = useState<DateRangeUnit>(initialUnit)

  // Calculate date range based on current inputs
  const calculatedRange = useMemo(() => {
    const numValue = parseInt(value, 10)
    if (isNaN(numValue) || numValue <= 0) {
      return null
    }

    const now = new Date()
    let calculatedStart: Date

    switch (unit) {
      case "days":
        calculatedStart = subDays(now, numValue - 1)
        break
      case "weeks":
        calculatedStart = subWeeks(now, numValue)
        break
      case "months":
        calculatedStart = subMonths(now, numValue)
        break
      default:
        return null
    }

    return {
      startDate: startOfDay(calculatedStart),
      endDate: now,
      numValue,
    }
  }, [value, unit])

  // Generate display label
  const displayLabel = useMemo(() => {
    if (!calculatedRange) {
      return ""
    }

    const { numValue } = calculatedRange
    const unitInfo = TIME_UNITS.find((u) => u.key === unit)

    if (!unitInfo) {
      return ""
    }

    const unitLabel = numValue === 1 ? unitInfo.singular : unitInfo.plural
    return `Last ${numValue} ${unitLabel}`
  }, [calculatedRange, unit])

  const isValid = useMemo(() => {
    const numValue = parseInt(value, 10)
    return !isNaN(numValue) && numValue > 0 && numValue <= MAX_UNITS
  }, [value])

  const handleValueChange = (newValue: string) => {
    // Only allow positive integers, up to three digits
    const sanitized = newValue.replace(/[^0-9]/g, "")
    if (sanitized.length <= 3) {
      setValue(sanitized)
    }
  }

  const handleApply = () => {
    if (!calculatedRange || !isValid) {
      return
    }

    onDateRangeSelect({
      type: "dynamic",
      value: calculatedRange.numValue,
      unit,
      displayLabel,
      startDate: calculatedRange.startDate,
      endDate: calculatedRange.endDate,
    })
    handleClose()
  }

  // Reset the form whenever the modal closes, however it was closed.
  const handleClose = () => {
    setValue(initialValue.toString())
    setUnit(initialUnit)
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        }
      }}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="size-4" />
            Dynamic Date Range
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="dynamic-range-value">Number</Label>
              <Input
                id="dynamic-range-value"
                value={value}
                onChange={(event) => handleValueChange(event.target.value)}
                startAdornment={<Hash className="size-4" />}
                placeholder="30"
                inputMode="numeric"
                aria-invalid={!isValid && value !== ""}
              />
              {!isValid && value !== "" && (
                <p className="text-xs text-coval-danger-text">
                  Enter a number between 1 and {MAX_UNITS}
                </p>
              )}
            </div>

            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="dynamic-range-unit">Time unit</Label>
              <Select
                value={unit}
                onValueChange={(next) => setUnit(next as DateRangeUnit)}
              >
                <SelectTrigger id="dynamic-range-unit" aria-label="Time unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_UNITS.map((timeUnit) => (
                    <SelectItem key={timeUnit.key} value={timeUnit.key}>
                      {timeUnit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {calculatedRange && isValid && (
            <div className="rounded-lg bg-surface-1 p-3">
              <p className="mb-1 text-sm text-coval-text-muted">Preview:</p>
              <p className="mb-1 text-sm font-medium">{displayLabel}</p>
              <p className="text-xs text-coval-text-muted">
                {format(calculatedRange.startDate, "MMM d, yyyy")} -{" "}
                {format(calculatedRange.endDate, "MMM d, yyyy")}
              </p>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!isValid || !calculatedRange}>
            Apply Range
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
